import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Clock,
  Phone,
  Plus,
  Trash2,
  User,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_API_URL}/api`;

const STATUS_STYLES = {
  scheduled: { color: "bg-blue-500", textColor: "text-blue-400", label: "Scheduled" },
  in_progress: { color: "bg-amber-500", textColor: "text-amber-400", label: "In Progress" },
  completed: { color: "bg-emerald-500", textColor: "text-emerald-400", label: "Completed" },
  missed: { color: "bg-red-500", textColor: "text-red-400", label: "Missed" },
  cancelled: { color: "bg-gray-500", textColor: "text-gray-400", label: "Cancelled" }
};

export default function CallScheduling() {
  const { getAuthHeader } = useAuth();
  
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [upcomingCalls, setUpcomingCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  
  // Schedule form
  const [newCall, setNewCall] = useState({
    contact_id: "",
    agent_id: "",
    date: new Date(),
    time: "09:00",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scheduledRes, upcomingRes, contactsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/calls/scheduled`, { headers: getAuthHeader() }),
        axios.get(`${API}/calls/upcoming?minutes_ahead=1440`, { headers: getAuthHeader() }),
        axios.get(`${API}/phonebook/contacts?limit=100`, { headers: getAuthHeader() }),
        axios.get(`${API}/agents`, { headers: getAuthHeader() })
      ]);
      
      setScheduledCalls(scheduledRes.data.scheduled_calls || []);
      setUpcomingCalls(upcomingRes.data.upcoming_calls || []);
      setContacts(contactsRes.data.contacts || []);
      setAgents(agentsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleCall = async () => {
    if (!newCall.contact_id || !newCall.agent_id) {
      toast.error("Please select a contact and agent");
      return;
    }
    
    setScheduling(true);
    try {
      // Combine date and time
      const scheduledTime = new Date(newCall.date);
      const [hours, minutes] = newCall.time.split(":");
      scheduledTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      await axios.post(`${API}/calls/schedule`, {
        contact_id: newCall.contact_id,
        agent_id: newCall.agent_id,
        scheduled_time: scheduledTime.toISOString(),
        notes: newCall.notes || null
      }, { headers: getAuthHeader() });
      
      toast.success("Call scheduled successfully");
      setScheduleDialogOpen(false);
      setNewCall({
        contact_id: "",
        agent_id: "",
        date: new Date(),
        time: "09:00",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule call");
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelCall = async (callId) => {
    try {
      await axios.delete(`${API}/calls/scheduled/${callId}`, { headers: getAuthHeader() });
      toast.success("Call cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel call");
    }
  };

  // Get week days
  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(weekStart, { weekStartsOn: 1 })
  });

  // Get calls for a specific day
  const getCallsForDay = (date) => {
    return scheduledCalls.filter(call => {
      const callDate = parseISO(call.scheduled_time);
      return isSameDay(callDate, date);
    });
  };

  // Get calls for selected date
  const selectedDateCalls = getCallsForDay(selectedDate);

  const navigateWeek = (direction) => {
    setWeekStart(prev => addDays(prev, direction * 7));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="call-scheduling-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
            Call Scheduling
          </h1>
          <p className="text-muted-foreground">
            Schedule and manage upcoming calls with your contacts
          </p>
        </div>
        <Button onClick={() => setScheduleDialogOpen(true)} data-testid="schedule-call-btn">
          <Plus className="w-4 h-4 mr-2" />
          Schedule Call
        </Button>
      </div>

      {/* Upcoming Calls Alert */}
      {upcomingCalls.length > 0 && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium">
                  {upcomingCalls.length} call{upcomingCalls.length !== 1 ? 's' : ''} scheduled in the next 24 hours
                </p>
                <p className="text-sm text-muted-foreground">
                  Next: {upcomingCalls[0]?.contact_name} at {format(parseISO(upcomingCalls[0]?.scheduled_time), "h:mm a")}
                </p>
              </div>
              <Button variant="outline" size="sm">
                View All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Week View */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Week View
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium min-w-[180px] text-center">
                  {format(weekStart, "MMM d")} - {format(endOfWeek(weekStart, { weekStartsOn: 1 }), "MMM d, yyyy")}
                </span>
                <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day) => {
                const dayCalls = getCallsForDay(day);
                const isSelected = isSameDay(day, selectedDate);
                const today = isToday(day);
                
                return (
                  <button
                    key={day.toISOString()}
                    className={cn(
                      "p-2 rounded-lg text-left transition-colors min-h-[100px]",
                      isSelected ? "bg-primary/10 ring-2 ring-primary" : "hover:bg-muted",
                      today && !isSelected && "bg-muted/50"
                    )}
                    onClick={() => setSelectedDate(day)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={cn(
                        "text-xs font-medium",
                        today && "text-primary"
                      )}>
                        {format(day, "EEE")}
                      </span>
                      <span className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center text-sm",
                        today && "bg-primary text-primary-foreground",
                        isSelected && !today && "bg-muted"
                      )}>
                        {format(day, "d")}
                      </span>
                    </div>
                    
                    {/* Call indicators */}
                    <div className="space-y-1">
                      {dayCalls.slice(0, 3).map((call) => (
                        <div
                          key={call.id}
                          className={cn(
                            "text-xs p-1 rounded truncate",
                            STATUS_STYLES[call.status]?.color || "bg-blue-500",
                            "text-white"
                          )}
                        >
                          {format(parseISO(call.scheduled_time), "h:mm a")}
                        </div>
                      ))}
                      {dayCalls.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{dayCalls.length - 3} more
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Day Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {format(selectedDate, "EEEE, MMM d")}
            </CardTitle>
            <CardDescription>
              {selectedDateCalls.length} call{selectedDateCalls.length !== 1 ? 's' : ''} scheduled
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedDateCalls.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No calls scheduled</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => {
                    setNewCall({...newCall, date: selectedDate});
                    setScheduleDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Call
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateCalls.map((call) => {
                  const status = STATUS_STYLES[call.status] || STATUS_STYLES.scheduled;
                  const agent = agents.find(a => a.id === call.agent_id);
                  
                  return (
                    <div
                      key={call.id}
                      className="p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{call.contact_name}</p>
                          <p className="text-sm text-muted-foreground font-mono">
                            {call.contact_phone}
                          </p>
                        </div>
                        <Badge className={status.color}>
                          {status.label}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(parseISO(call.scheduled_time), "h:mm a")}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {agent?.name || "Unknown"}
                        </span>
                      </div>
                      
                      {call.notes && (
                        <p className="text-xs text-muted-foreground mb-2">
                          {call.notes}
                        </p>
                      )}
                      
                      {call.status === "scheduled" && (
                        <div className="flex gap-2">
                          <Button size="sm" className="flex-1">
                            <Phone className="w-3 h-3 mr-1" />
                            Call Now
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleCancelCall(call.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Scheduled Calls List */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>All Scheduled Calls</CardTitle>
          <CardDescription>
            Complete list of all scheduled calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          {scheduledCalls.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No calls scheduled yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {scheduledCalls.slice(0, 10).map((call) => {
                const status = STATUS_STYLES[call.status] || STATUS_STYLES.scheduled;
                const agent = agents.find(a => a.id === call.agent_id);
                
                return (
                  <div
                    key={call.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        status.color
                      )} />
                      <div>
                        <p className="font-medium">{call.contact_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(parseISO(call.scheduled_time), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{agent?.name || "Unknown"}</Badge>
                      <Badge className={status.color}>{status.label}</Badge>
                      {call.status === "scheduled" && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleCancelCall(call.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Call Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Schedule a Call
            </DialogTitle>
            <DialogDescription>
              Set up a scheduled call with a contact
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Contact Selection */}
            <div className="space-y-2">
              <Label>Contact *</Label>
              <Select
                value={newCall.contact_id}
                onValueChange={(v) => setNewCall({...newCall, contact_id: v})}
              >
                <SelectTrigger data-testid="contact-select">
                  <SelectValue placeholder="Select a contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No contacts available
                    </div>
                  ) : (
                    contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} ({contact.phone})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Agent Selection */}
            <div className="space-y-2">
              <Label>AI Agent *</Label>
              <Select
                value={newCall.agent_id}
                onValueChange={(v) => setNewCall({...newCall, agent_id: v})}
              >
                <SelectTrigger data-testid="agent-select">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(newCall.date, "MMM d, yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newCall.date}
                      onSelect={(date) => date && setNewCall({...newCall, date})}
                      disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={newCall.time}
                  onChange={(e) => setNewCall({...newCall, time: e.target.value})}
                  data-testid="time-input"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add any notes about this call..."
                value={newCall.notes}
                onChange={(e) => setNewCall({...newCall, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setScheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleScheduleCall} disabled={scheduling}>
              {scheduling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Schedule Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
